/**
 * login_v3.js — DAS-108 Phase 06: Login V3 · 정보 구조 재설계 시안 매칭
 *
 * 매핑: LoginDesktopV2 + LoginMobileV2 → 컨텍스트 좌·폼 우 분할 레이아웃
 *
 * 인증 로직: LoginPageV1 (pages/login.js)의 onSubmit / 상태 구조 그대로 재사용.
 * 디자인 시스템 토큰 전용. raw hex 금지. arbitrary Tailwind 금지 (min-h-[44px] 예외).
 * primitives: Btn, Input, HexLogo, V2Icon, StatusDot 적극 활용 (DAS-102).
 * a11y: form input 필수 label/aria, password 토글 aria-label/aria-pressed 필수.
 */

import React from 'react';
import { loginUser } from '../../services/balanceApi';
import { consumeSessionExpired, setLoginSession, getStoredUsername } from '../../utils/auth';
import { isMobileViewport } from '../../utils/responsive';
import { HexLogo, V2Icon, StatusDot, Btn, Input } from '../common/claude_primitives';

/* ── 배경 격자 ───────────────────────────────────────────── */
function BgGrid() {
    return (
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{
                position: 'absolute', inset: 0,
                background: 'radial-gradient(ellipse at 50% 30%, color-mix(in oklch, var(--color-content) 3%, transparent), transparent 55%)',
            }} />
            <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.3 }}>
                <defs>
                    <pattern id="v3-login-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--color-surface-border)" strokeWidth="0.5" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#v3-login-grid)" />
            </svg>
        </div>
    );
}

/* ── 인라인 에러 ─────────────────────────────────────────── */
function InlineError({ message }) {
    if (!message) return null;
    return (
        <div
            role="alert"
            className="rounded-lg border border-loss/20 bg-loss-subtle px-3 py-2.5 text-sm text-loss"
        >
            {message}
        </div>
    );
}

/* ── 커스텀 체크박스 ──────────────────────────────────────── */
function Checkbox({ checked, onClick, label, id }) {
    return (
        <label
            htmlFor={id}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', minHeight: 44, paddingTop: 4, paddingBottom: 4 }}
        >
            <button
                id={id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={onClick}
                style={{
                    width: 16, height: 16, borderRadius: 4,
                    border: checked ? '1px solid var(--color-brand)' : '1px solid var(--color-surface-border-light)',
                    background: checked ? 'var(--color-brand)' : 'var(--color-surface-elevated)',
                    display: 'grid', placeItems: 'center', flexShrink: 0, cursor: 'pointer',
                    transition: 'background 0.15s, border-color 0.15s',
                }}
            >
                {checked && <V2Icon name="check" size={10} style={{ color: 'var(--color-content-inverse)' }} />}
            </button>
            <span style={{ fontSize: 12, color: 'var(--color-content-secondary)' }}>{label}</span>
        </label>
    );
}

/* ── 입력 필드 레이블 ────────────────────────────────────── */
function FieldLabel({ htmlFor, children }) {
    return (
        <label
            htmlFor={htmlFor}
            style={{
                display: 'block',
                fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'var(--color-content-muted)', marginBottom: 6, fontWeight: 500,
                fontFamily: 'JetBrains Mono, monospace',
            }}
        >
            {children}
        </label>
    );
}

/* ── KPI 통계 항목 ───────────────────────────────────────── */
const KPI_STATS = [
    { l: '운영 중 전략', v: '3' },
    { l: '연동 거래소', v: '8' },
    { l: '수집 안정도', v: '99.94%' },
];

/* ── 데스크톱 V2 (정보 구조 재설계): 컨텍스트 좌 · 폼 우 분할 ─── */
function LoginDesktopV2({ state, handlers }) {
    const { username, password, remember, error, isSubmitting, showPassword } = state;
    const { onUsernameChange, onPasswordChange, onRememberToggle, onShowPasswordToggle, onSubmit } = handlers;

    return (
        <div style={{
            minHeight: '100vh',
            display: 'grid',
            gridTemplateColumns: '1.1fr 1fr',
            position: 'relative',
        }}>
            {/* 좌: 컨텍스트 패널 */}
            <div style={{
                position: 'relative', padding: '56px 56px 40px',
                display: 'flex', flexDirection: 'column',
                background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface))',
                borderRight: '1px solid var(--color-surface-border)',
            }}>
                <BgGrid />

                {/* 로고 */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        border: '1.5px solid var(--color-content)',
                        display: 'grid', placeItems: 'center',
                    }}>
                        <HexLogo size={20} stroke="var(--color-content)" accent="var(--color-brand)" />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em', color: 'var(--color-content)' }}>
                        MyDashboard{' '}
                        <span style={{ fontWeight: 500, color: 'var(--color-content-muted)' }}>v2</span>
                    </div>
                </div>

                {/* 마케팅 카피 */}
                <div style={{ position: 'relative', marginTop: 'auto', marginBottom: 40 }}>
                    <div style={{
                        fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                        color: 'var(--color-content-muted)', marginBottom: 14,
                        fontFamily: 'JetBrains Mono, monospace',
                    }}>
                        Portfolio Control
                    </div>
                    <h2 style={{
                        margin: 0, fontSize: 32, fontWeight: 600,
                        letterSpacing: '-0.02em', lineHeight: 1.15,
                        color: 'var(--color-content)',
                    }}>
                        크로스 거래소 자산을<br />한 화면에서 운영합니다.
                    </h2>
                    <p style={{
                        marginTop: 14, color: 'var(--color-content-secondary)',
                        fontSize: 13, lineHeight: 1.6, maxWidth: 440,
                    }}>
                        3개 전략 · 8개 거래소의 포지션, 수집기 파이프라인, 점검 일정을 실시간으로 추적하세요.
                    </p>
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 14, marginTop: 32,
                    }}>
                        {KPI_STATS.map((k) => (
                            <div key={k.l}>
                                <div style={{
                                    fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
                                    color: 'var(--color-content-muted)', marginBottom: 4,
                                    fontFamily: 'JetBrains Mono, monospace',
                                }}>
                                    {k.l}
                                </div>
                                <div style={{
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em',
                                    color: 'var(--color-content)',
                                }}>
                                    {k.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 서비스 상태 */}
                <div style={{
                    position: 'relative', display: 'flex', alignItems: 'center', gap: 10,
                    color: 'var(--color-content-muted)', fontSize: 11,
                    fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.08em',
                }}>
                    <StatusDot tone="gain" size={6} />
                    SERVICES · OPERATIONAL
                </div>
            </div>

            {/* 우: 폼 */}
            <div style={{
                padding: '56px 64px',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                background: 'var(--color-surface)',
            }}>
                <form
                    aria-label="로그인 폼"
                    onSubmit={onSubmit}
                    style={{
                        maxWidth: 400, width: '100%', margin: '0 auto',
                        display: 'flex', flexDirection: 'column', gap: 20,
                    }}
                >
                    {/* 헤딩 */}
                    <div>
                        <div style={{
                            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                            color: 'var(--color-content-muted)', marginBottom: 8,
                            fontFamily: 'JetBrains Mono, monospace',
                        }}>
                            로그인
                        </div>
                        <h3 style={{
                            margin: 0, fontSize: 22, fontWeight: 600,
                            letterSpacing: '-0.015em', color: 'var(--color-content)',
                        }}>
                            다시 만나서 반가워요
                        </h3>
                        <p style={{
                            margin: '6px 0 0', color: 'var(--color-content-secondary)', fontSize: 13,
                        }}>
                            계정 정보를 입력해 대시보드로 이동하세요.
                        </p>
                    </div>

                    <InlineError message={error} />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* USERNAME */}
                        <div>
                            <FieldLabel htmlFor="v3d-username">Username</FieldLabel>
                            <Input
                                id="v3d-username"
                                type="text"
                                value={username}
                                onChange={onUsernameChange}
                                placeholder="사용자명을 입력하세요"
                                autoComplete="username"
                            />
                        </div>

                        {/* PASSWORD */}
                        <div>
                            <FieldLabel htmlFor="v3d-password">Password</FieldLabel>
                            <div style={{ position: 'relative' }}>
                                <Input
                                    id="v3d-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={onPasswordChange}
                                    placeholder="••••••••••••"
                                    autoComplete="current-password"
                                    style={{ paddingRight: '2.5rem' }}
                                />
                                <button
                                    type="button"
                                    onClick={onShowPasswordToggle}
                                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                                    aria-pressed={showPassword}
                                    style={{
                                        position: 'absolute', right: 10, top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                        color: 'var(--color-content-muted)',
                                        display: 'flex', alignItems: 'center',
                                    }}
                                >
                                    <V2Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
                                </button>
                            </div>
                        </div>

                        {/* 기억하기 + 비밀번호 찾기 */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Checkbox
                                id="v3d-remember"
                                checked={remember}
                                onClick={onRememberToggle}
                                label="30일간 로그인 유지"
                            />
                            <button
                                type="button"
                                disabled
                                aria-disabled="true"
                                style={{
                                    fontSize: 12, color: 'var(--color-content-muted)',
                                    textDecoration: 'none', fontFamily: 'JetBrains Mono, monospace',
                                    border: 'none', background: 'transparent', padding: 0,
                                    cursor: 'not-allowed', opacity: 0.5,
                                }}
                            >
                                비밀번호 찾기 →
                            </button>
                        </div>

                        {/* 제출 버튼 */}
                        <Btn
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={isSubmitting}
                            style={{ width: '100%', justifyContent: 'center', minHeight: 44 }}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-content-inverse/30 border-t-content-inverse" />
                                    로그인 중...
                                </>
                            ) : (
                                <>
                                    로그인
                                    <V2Icon name="arrowR" size={14} />
                                </>
                            )}
                        </Btn>
                    </div>

                    {/* 구분선 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, height: 1, background: 'var(--color-surface-border)' }} />
                        <span style={{
                            fontSize: 10, letterSpacing: '0.1em',
                            color: 'var(--color-content-muted)',
                            fontFamily: 'JetBrains Mono, monospace',
                        }}>
                            OR
                        </span>
                        <div style={{ flex: 1, height: 1, background: 'var(--color-surface-border)' }} />
                    </div>

                    {/* SSO 버튼 (비활성) */}
                    <Btn
                        type="button"
                        variant="outline"
                        size="md"
                        disabled
                        icon="key"
                        style={{ width: '100%', justifyContent: 'center', opacity: 0.6, cursor: 'not-allowed' }}
                    >
                        SSO · 회사 계정으로 로그인
                    </Btn>

                    {/* 안내 문구 */}
                    <div style={{
                        fontSize: 12, textAlign: 'center',
                        color: 'var(--color-content-muted)', marginTop: 4,
                    }}>
                        내부 사용자 전용입니다. 계정이 필요하시면{' '}
                        <span
                            style={{ color: 'var(--color-content)', textDecoration: 'underline', textUnderlineOffset: 3 }}
                        >
                            관리자
                        </span>
                        에게 문의하세요.
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ── 모바일 V2 (정보 구조 재설계): 헤더 컨텍스트 · 폼 스택 ───── */
function LoginMobileV2({ state, handlers }) {
    const { username, password, remember, error, isSubmitting, showPassword } = state;
    const { onUsernameChange, onPasswordChange, onRememberToggle, onShowPasswordToggle, onSubmit } = handlers;

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--color-surface)' }}>
            <BgGrid />

            {/* 헤더 컨텍스트 */}
            <div style={{
                position: 'relative',
                padding: '34px 22px 28px',
                display: 'flex', flexDirection: 'column', gap: 12,
                borderBottom: '1px solid var(--color-surface-border)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 9,
                        border: '1.5px solid var(--color-content)',
                        display: 'grid', placeItems: 'center',
                    }}>
                        <HexLogo size={18} stroke="var(--color-content)" accent="var(--color-brand)" />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-content)' }}>
                        MyDashboard{' '}
                        <span style={{ fontWeight: 500, color: 'var(--color-content-muted)' }}>v2</span>
                    </div>
                </div>
                <div style={{
                    fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
                    color: 'var(--color-content-muted)', marginTop: 8,
                    fontFamily: 'JetBrains Mono, monospace',
                }}>
                    Portfolio Control
                </div>
                <h2 style={{
                    margin: 0, fontSize: 22, fontWeight: 600,
                    letterSpacing: '-0.015em', lineHeight: 1.2,
                    color: 'var(--color-content)',
                }}>
                    크로스 거래소 자산을<br />한 화면에서.
                </h2>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    color: 'var(--color-content-muted)', fontSize: 12,
                    fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.08em',
                    marginTop: 6,
                }}>
                    <StatusDot tone="gain" size={5} />
                    OPERATIONAL
                </div>
            </div>

            {/* 폼 영역 */}
            <form
                aria-label="로그인 폼"
                onSubmit={onSubmit}
                style={{
                    position: 'relative',
                    padding: '28px 22px',
                    display: 'flex', flexDirection: 'column', gap: 14, flex: 1,
                }}
            >
                <div>
                    <div style={{
                        fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                        color: 'var(--color-content-muted)', marginBottom: 6,
                        fontFamily: 'JetBrains Mono, monospace',
                    }}>
                        로그인
                    </div>
                    <h3 style={{
                        margin: 0, fontSize: 17, fontWeight: 600,
                        color: 'var(--color-content)',
                    }}>
                        다시 만나서 반가워요
                    </h3>
                </div>

                <InlineError message={error} />

                {/* USERNAME */}
                <div>
                    <FieldLabel htmlFor="v3m-username">Username</FieldLabel>
                    <Input
                        id="v3m-username"
                        type="text"
                        value={username}
                        onChange={onUsernameChange}
                        placeholder="사용자명을 입력하세요"
                        autoComplete="username"
                    />
                </div>

                {/* PASSWORD */}
                <div>
                    <FieldLabel htmlFor="v3m-password">Password</FieldLabel>
                    <div style={{ position: 'relative' }}>
                        <Input
                            id="v3m-password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={onPasswordChange}
                            placeholder="••••••••••"
                            autoComplete="current-password"
                            style={{ paddingRight: '2.5rem' }}
                        />
                        <button
                            type="button"
                            onClick={onShowPasswordToggle}
                            aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                            aria-pressed={showPassword}
                            style={{
                                position: 'absolute', right: 10, top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                color: 'var(--color-content-muted)',
                                display: 'flex', alignItems: 'center',
                            }}
                        >
                            <V2Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
                        </button>
                    </div>
                </div>

                {/* 기억하기 + 비밀번호 찾기 */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Checkbox
                        id="v3m-remember"
                        checked={remember}
                        onClick={onRememberToggle}
                        label="유지"
                    />
                    <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        style={{
                            fontSize: 12, color: 'var(--color-content-muted)',
                            textDecoration: 'none', fontFamily: 'JetBrains Mono, monospace',
                            border: 'none', background: 'transparent', padding: 0,
                            cursor: 'not-allowed', opacity: 0.5,
                        }}
                    >
                        비밀번호 찾기 →
                    </button>
                </div>

                {/* 제출 버튼 */}
                <Btn
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isSubmitting}
                    style={{ width: '100%', justifyContent: 'center', minHeight: 48 }}
                >
                    {isSubmitting ? (
                        <>
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-content-inverse/30 border-t-content-inverse" />
                            로그인 중...
                        </>
                    ) : (
                        <>
                            로그인
                            <V2Icon name="arrowR" size={14} />
                        </>
                    )}
                </Btn>

                {/* SSO 버튼 (비활성) */}
                <Btn
                    type="button"
                    variant="outline"
                    size="md"
                    disabled
                    icon="key"
                    style={{ width: '100%', justifyContent: 'center', minHeight: 44, opacity: 0.6, cursor: 'not-allowed' }}
                >
                    SSO
                </Btn>
            </form>
        </div>
    );
}

/* ── 메인 export: LoginPageV3 ───────────────────────────── */
class LoginPageV3 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            username: '',
            password: '',
            remember: true,
            error: '',
            isSubmitting: false,
            showPassword: false,
            isMobile: isMobileViewport(),
        };
        this._onResize = this._onResize.bind(this);
    }

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        const storedUsername = getStoredUsername();
        const sessionExpired = consumeSessionExpired();
        this.setState({
            username: storedUsername || '',
            error: sessionExpired ? '세션이 만료되었습니다. 다시 로그인해주세요.' : '',
        });
        window.addEventListener('resize', this._onResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onResize);
    }

    onUsernameChange = (e) => this.setState({ username: e.target.value });
    onPasswordChange = (e) => this.setState({ password: e.target.value });
    onRememberToggle = () => this.setState((prev) => ({ remember: !prev.remember }));
    onShowPasswordToggle = () => this.setState((prev) => ({ showPassword: !prev.showPassword }));

    onSubmit = async (e) => {
        e.preventDefault();
        const { username, password } = this.state;
        if (!username || !password) {
            this.setState({ error: '아이디와 비밀번호를 입력하세요.' });
            return;
        }
        this.setState({ isSubmitting: true, error: '' });
        try {
            const response = await loginUser(username, password);
            if (response.status === 200) {
                setLoginSession(username, response.data.access, response.data.refresh);
                window.location.href = '/dashboard';
            } else {
                this.setState({ error: '아이디 또는 비밀번호를 확인해주세요.' });
            }
        } catch (err) {
            console.error('LoginV3 error:', err);
            this.setState({ error: '로그인 중 오류가 발생했습니다.' });
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    render() {
        const { isMobile } = this.state;
        const handlers = {
            onUsernameChange: this.onUsernameChange,
            onPasswordChange: this.onPasswordChange,
            onRememberToggle: this.onRememberToggle,
            onShowPasswordToggle: this.onShowPasswordToggle,
            onSubmit: this.onSubmit,
        };

        return isMobile
            ? <LoginMobileV2 state={this.state} handlers={handlers} />
            : <LoginDesktopV2 state={this.state} handlers={handlers} />;
    }
}

export default LoginPageV3;
